import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  MessageCircle, User, Clock, Phone, ArrowLeft 
} from 'lucide-react';
import { useCustomerAuth, customerApi } from '../../contexts/CustomerAuthContext';
import CustomerHeader from './CustomerHeader';
import Footer from '../public/Footer';
import { BACKEND_BASE_URL, normalizeImageUrl } from '../../utils/api';
import './CustomerDashboard.css';

interface Conversation {
  other_party_id: number;
  other_party_type: string;
  first_name: string;
  last_name: string;
  profile_picture: string;
  last_message_time: string;
  unread_count: number;
}

interface MessageItem {
  id: number;
  sender_id: number;
  sender_type: string;
  recipient_id: number;
  recipient_type: string;
  subject: string;
  message: string;
  service_id?: number;
  status: string;
  is_read: boolean;
  created_at: string;
}

const CustomerMessages: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeThread, setActiveThread] = useState<MessageItem[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [selectedOtherId, setSelectedOtherId] = useState<number | null>(null);
  const [composeText, setComposeText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { customer, isAuthenticated } = useCustomerAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchConversations();
    // Preselect conversation if otherId is provided
    const otherIdParam = searchParams.get('otherId');
    if (otherIdParam) {
      const id = parseInt(otherIdParam, 10);
      if (!isNaN(id)) {
        handleOpenThread(id);
      }
    }
  }, [isAuthenticated, navigate]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await customerApi.get('/messages/conversations');
      
      if (response.data.success) {
        setConversations(response.data.conversations || []);
      } else {
        setError('Failed to load conversations');
      }
    } catch (error: any) {
      console.error('Error fetching conversations:', error);
      setError(error.response?.data?.message || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenThread = async (otherId: number) => {
    try {
      setThreadLoading(true);
      setSelectedOtherId(otherId);
      const response = await customerApi.get(`/messages/thread?otherId=${otherId}&limit=50&page=1`);
      if (response.data.success) {
        setActiveThread(response.data.messages || []);
      }
    } catch (e) {
      console.error('Error fetching thread:', e);
    } finally {
      setThreadLoading(false);
    }
  };

  const handleSend = async () => {
    if (!selectedOtherId || !composeText.trim()) return;
    try {
      setSending(true);
      // Derive a subject from the last message or fallback
      const lastSubject = activeThread.length > 0 ? activeThread[activeThread.length - 1].subject : 'Direct Message';
      const response = await customerApi.post('/messages/send', {
        recipientId: selectedOtherId,
        recipientType: 'talent',
        subject: lastSubject?.startsWith('Re:') ? lastSubject : `Re: ${lastSubject || 'Conversation'}`,
        message: composeText.trim(),
      });
      if (response.data.success) {
        // Append sent message locally for instant feedback
        setActiveThread(prev => ([
          ...prev,
          {
            id: response.data.messageId || Date.now(),
            sender_id: customer?.id || 0,
            sender_type: 'customer',
            recipient_id: selectedOtherId,
            recipient_type: 'talent',
            subject: lastSubject?.startsWith('Re:') ? lastSubject : `Re: ${lastSubject || 'Conversation'}`,
            message: composeText.trim(),
            service_id: undefined,
            status: 'sent',
            is_read: true,
            created_at: new Date().toISOString(),
          }
        ]));
        setComposeText('');
        // Refresh conversations sidebar to update last message time
        fetchConversations();
      }
    } catch (e) {
      console.error('Error sending message:', e);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return 'Today';
    } else if (diffDays === 2) {
      return 'Yesterday';
    } else if (diffDays <= 7) {
      return `${diffDays - 1} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getProfileImage = (profilePicture: string | null) => {
    return normalizeImageUrl(profilePicture, '/assets/images/default-avatar.png');
  };

  if (!isAuthenticated) {
    return <div>Redirecting to login...</div>;
  }

  return (
    <>
      <CustomerHeader />
      <div className="customer-dashboard-container">
        <div className="container">
          <div className="dashboard-header">
            <button 
              onClick={() => navigate('/customer/dashboard')}
              className="back-button"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'none',
                border: 'none',
                color: '#666',
                cursor: 'pointer',
                fontSize: '14px',
                marginBottom: '20px'
              }}
            >
              <ArrowLeft size={18} />
              Back to Dashboard
            </button>
            
            <h1>My Messages</h1>
            <p>View and manage your conversations with service providers</p>
          </div>

          <div className="messages-container">
            {loading && (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading conversations...</p>
              </div>
            )}

            {error && (
              <div className="error-state">
                <MessageCircle size={48} />
                <h3>Error Loading Messages</h3>
                <p>{error}</p>
                <button 
                  onClick={fetchConversations}
                  className="retry-button"
                >
                  Try Again
                </button>
              </div>
            )}

            {!loading && !error && conversations.length === 0 && (
              <div className="empty-state">
                <MessageCircle size={64} />
                <h3>No Messages Yet</h3>
                <p>Start a conversation by contacting a service provider</p>
                <button 
                  onClick={() => navigate('/services')}
                  className="cta-button"
                >
                  Browse Services
                </button>
              </div>
            )}

            {!loading && !error && conversations.length > 0 && (
              <div className="conversations-list">
                {conversations.map((conversation) => (
                  <div 
                    key={`${conversation.other_party_type}-${conversation.other_party_id}`}
                    className={`conversation-card ${selectedOtherId === conversation.other_party_id ? 'active' : ''}`}
                    onClick={() => handleOpenThread(conversation.other_party_id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="conversation-avatar">
                      <img 
                        src={getProfileImage(conversation.profile_picture)}
                        alt={`${conversation.first_name} ${conversation.last_name}`}
                        onError={(e) => {
                          e.currentTarget.src = '/assets/images/default-avatar.png';
                        }}
                      />
                      {conversation.other_party_type === 'talent' && (
                        <div className="user-type-badge">
                          <User size={12} />
                        </div>
                      )}
                    </div>

                    <div className="conversation-content">
                      <div className="conversation-header">
                        <h4 className="conversation-name">
                          {conversation.first_name} {conversation.last_name}
                        </h4>
                        {conversation.unread_count > 0 && (
                          <span className="unread-badge">
                            {conversation.unread_count}
                          </span>
                        )}
                      </div>

                      <div className="conversation-meta">
                        <span className="conversation-type">
                          Service Provider
                        </span>
                        <span className="conversation-time">
                          <Clock size={14} />
                          {formatDate(conversation.last_message_time)}
                        </span>
                      </div>
                    </div>

                    <div />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Thread view */}
      <div className="container" style={{ marginTop: 20 }}>
        {selectedOtherId && (
          <div className="admin-card">
            <div className="card-header">
              <div className="card-title">Conversation</div>
            </div>
            {threadLoading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading messages...</p>
              </div>
            ) : (
              <div style={{ maxHeight: 400, overflowY: 'auto', padding: 12, display: 'grid', gap: 8 }}>
                {activeThread.map(msg => (
                  <div key={msg.id} style={{
                    justifySelf: msg.sender_type === 'customer' ? 'end' : 'start',
                    background: msg.sender_type === 'customer' ? '#e6f2ff' : '#f8f9fa',
                    border: '1px solid var(--border-medium)',
                    padding: 10,
                    borderRadius: 8,
                    maxWidth: '75%'
                  }}>
                    <div style={{ fontSize: 12, color: 'var(--text-medium)' }}>{new Date(msg.created_at).toLocaleString()}</div>
                    <div style={{ fontWeight: 600 }}>{msg.subject}</div>
                    <div>{msg.message}</div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: '1px solid var(--border-medium)' }}>
              <input
                type="text"
                className="form-control"
                value={composeText}
                onChange={(e) => setComposeText(e.target.value)}
                placeholder="Type a message..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
              />
              <button className="btn btn-primary" onClick={handleSend} disabled={sending || !composeText.trim()}>
                {sending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default CustomerMessages;
